import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { UserPlus, UserCheck, Users } from 'lucide-react';
import {
  UniversalPage,
  UniversalTable,
  type UniversalTableColumn,
  Button,
  Card,
  PeriodFilter,
  SearchField,
  StatusBadge,
  TABLE_PRESETS,
  type StatusTone,
} from '@sighthost/ui';
import {
  adminCustomerService,
  type AdminCustomerSummaryRow,
  type LifecycleStage,
} from '@/domains/customers/services/adminCustomerService';
import { useTableFilters } from '@/shared/hooks/useTableFilters';
import { formatZAR } from '@/shared/utils/format';

const LIFECYCLE_OPTIONS: { value: LifecycleStage | 'all'; label: string }[] = [
  { value: 'all', label: 'All stages' },
  { value: 'lead', label: 'Lead' },
  { value: 'active', label: 'Active' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'cancellation_pending', label: 'Cancellation pending' },
  { value: 'churned', label: 'Churned' },
];

const STAGE_TONE: Record<LifecycleStage, StatusTone> = {
  lead: 'info',
  active: 'active',
  suspended: 'overdue',
  cancellation_pending: 'pending',
  churned: 'draft',
};

const STAGE_LABEL: Record<LifecycleStage, string> = {
  lead: 'Lead',
  active: 'Active',
  suspended: 'Suspended',
  cancellation_pending: 'Cancelling',
  churned: 'Churned',
};

function formatRelative(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const ms = Date.now() - d.getTime();
  const days = Math.floor(ms / 86400000);
  if (days < 1) return 'today';
  if (days === 1) return '1 day ago';
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  if (months === 1) return '1 month ago';
  if (months < 12) return `${months} months ago`;
  const years = Math.floor(days / 365);
  return years === 1 ? '1 year ago' : `${years} years ago`;
}

export default function CustomersListPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<AdminCustomerSummaryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [stageFilter, setStageFilter] = useState<LifecycleStage | 'all'>('all');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    adminCustomerService
      .listAll()
      .then((data) => {
        if (!cancelled) {
          setRows(data);
          setLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * A customer list is a STOCK set (Rule T-1), so the default period is
   * `all-time`: defaulting to this month would render 1 of 22 customers and
   * read as data loss rather than as a filter.
   *
   * The date control is labelled `Created`, never "Period" (Rule T-2) — the
   * label is the only thing stopping "customers" from silently becoming
   * "customers created in period", which is a different number that looks
   * exactly as legitimate.
   *
   * `dateMode: 'client'` is lawful under AR-5 option (a): `listAll()` carries
   * no LIMIT, so the fetched set is complete (95 profiles, measured
   * 2026-09-13).
   */
  const table = useTableFilters<AdminCustomerSummaryRow>({
    rows,
    dateMode: 'client',
    dateLabel: 'Created',
    dateOf: (r) => r.created_at,
    searchText: (r) => [r.email ?? '', r.full_name ?? '', r.company ?? ''].join(' '),
    facets: useMemo(
      () => [
        (r: AdminCustomerSummaryRow) =>
          stageFilter === 'all' || r.lifecycle_stage === stageFilter,
      ],
      [stageFilter],
    ),
  });

  const filtered = table.rows;

  const columns = useMemo<UniversalTableColumn<AdminCustomerSummaryRow>[]>(
    () => [
      {
        key: 'email',
        header: 'Email',
        sortValue: (r) => r.email ?? '',
        render: (r) => (
          <span className="font-medium text-foreground">{r.email ?? '—'}</span>
        ),
      },
      {
        key: 'name',
        header: 'Name',
        sortValue: (r) => r.full_name,
        render: (r) => (
          <div className="flex flex-col">
            <span className="text-foreground">{r.full_name}</span>
            {r.company && (
              <span className="text-xs text-muted-foreground">{r.company}</span>
            )}
          </div>
        ),
      },
      {
        key: 'stage',
        header: 'Stage',
        sortValue: (r) => r.lifecycle_stage,
        render: (r) => (
          <StatusBadge tone={STAGE_TONE[r.lifecycle_stage]} dot>
            {STAGE_LABEL[r.lifecycle_stage]}
          </StatusBadge>
        ),
      },
      {
        key: 'hosting',
        header: 'Hosting',
        align: 'right',
        sortValue: (r) => r.hosting_count,
        render: (r) => <span>{r.hosting_count}</span>,
      },
      {
        key: 'domains',
        header: 'Domains',
        align: 'right',
        sortValue: (r) => r.domain_count,
        render: (r) => <span>{r.domain_count}</span>,
        hideOnMobile: true,
      },
      {
        key: 'tickets',
        header: 'Open tickets',
        align: 'right',
        sortValue: (r) => r.open_tickets,
        render: (r) =>
          r.open_tickets > 0 ? (
            <span className="font-medium text-warning">{r.open_tickets}</span>
          ) : (
            <span className="text-muted-foreground">0</span>
          ),
        hideOnMobile: true,
      },
      {
        key: 'unpaid',
        header: 'Unpaid',
        align: 'right',
        sortValue: (r) => r.unpaid_total,
        render: (r) =>
          r.unpaid_total > 0 ? (
            <span className="font-medium text-danger">
              {formatZAR(r.unpaid_total)}
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        key: 'last_activity',
        header: 'Last activity',
        sortValue: (r) => (r.last_activity_at ? new Date(r.last_activity_at) : null),
        render: (r) => (
          <span className="text-muted-foreground">
            {formatRelative(r.last_activity_at)}
          </span>
        ),
        hideOnMobile: true,
      },
    ],
    []
  );

  return (
    <UniversalPage
      title="Customers"
      breadcrumbs={[{ label: 'Admin', href: '/admin' }, { label: 'Customers' }]}
      actions={
        <>
          <Link to="/admin/customers/import-existing">
            <Button variant="outline" size="sm">
              <UserCheck className="h-4 w-4" />
              Import existing
            </Button>
          </Link>
          <Link to="/sales/customers/new">
            <Button size="sm" variant="marketing" className="text-[11px]">
              <UserPlus className="h-4 w-4" />
              New customer
            </Button>
          </Link>
        </>
      }
    >
      <Card interactive={false} className="p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <label htmlFor="stage" className="text-sm text-muted-foreground">
              Stage
            </label>
            <select
              id="stage"
              value={stageFilter}
              onChange={(e) =>
                setStageFilter(e.target.value as LifecycleStage | 'all')
              }
              className="h-9 rounded border border-input bg-card px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              {LIFECYCLE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <PeriodFilter
            value={table.filters.period}
            from={table.filters.from}
            to={table.filters.to}
            onChange={table.filters.setPeriod}
            onStep={table.stepPeriodBy}
            presets={TABLE_PRESETS}
            variant="menu"
            steppable
            label="Created"
          />
          <SearchField
            value={table.q}
            onValueChange={table.setQ}
            pending={table.isSearching}
            placeholder="Search email, name, company"
            className="ml-auto w-full max-w-xs"
          />
        </div>
      </Card>

      <UniversalTable<AdminCustomerSummaryRow>
        data={filtered}
        columns={columns}
        getRowKey={(r) => r.customer_id}
        caption={loading ? undefined : table.caption}
        onRowClick={(r) => navigate(`/admin/customers/${r.customer_id}`)}
        isLoading={loading}
        error={error}
        empty={{
          icon: Users,
          title: rows.length === 0 ? 'No customers yet' : 'No matches',
          description:
            rows.length === 0
              ? 'Use the buttons above to add your first customer.'
              : 'Try a different stage or search term.',
        }}
      />
    </UniversalPage>
  );
}
